import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Sidebar from './Sidebar'
import { useFeedback } from '../../App'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [betaDismissed, setBetaDismissed] = useState(false)
  const { openFeedback } = useFeedback()

  const dismissBeta = () => setBetaDismissed(true)

  return (
    <div className="flex h-screen w-full overflow-hidden bg-neutral-900 text-neutral-100">

      {/* ── Mobile overlay backdrop (<768 px only) ───────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-neutral-950/70 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ──────────────────────────────────────────────────────────── */}
      {/*
       *  Mobile  (<768 px):  off-canvas drawer, slides in when sidebarOpen
       *  Tablet  (768–1023): always in-flow, icon-only 40px (w-10)
       *  Desktop (≥1024 px): always in-flow, full width 240px (w-60)
       */}
      <aside
        className={[
          'z-30 flex flex-shrink-0 flex-col',
          'h-full',
          'bg-neutral-900 border-r border-neutral-700/60',
          // Responsive widths
          'w-60 md:w-10 lg:w-60',
          // Mobile: fixed off-canvas with slide transition
          'fixed top-0 left-0 transition-transform duration-300 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Tablet+: always visible, relative in flow
          'md:relative md:translate-x-0',
        ].join(' ')}
        aria-label="Primary navigation"
      >
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </aside>

      {/* ── Main content area ────────────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">

        {/* Mobile top bar — visible only on small viewports (<768 px) */}
        <header className="flex items-center gap-3 px-4 py-3 border-b border-neutral-700/60 bg-neutral-900 md:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-fast"
            aria-label="Open navigation menu"
          >
            {/* Hamburger icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Link to="/" className="text-sm font-semibold tracking-wide text-neutral-100 hover:text-primary-300 transition-colors">DevHub</Link>
        </header>

        {/* Early access banner — dismissed per session */}
        {!betaDismissed && (
          <div className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-500/10 border-b border-amber-500/30 text-amber-300 text-xs">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span>DevHub is in early access — things may be unstable.</span>
              <button
                type="button"
                onClick={openFeedback}
                className="ml-1 underline underline-offset-2 cursor-pointer hover:text-amber-200 transition-colors"
              >
                Share feedback
              </button>
            </div>
            <button
              type="button"
              onClick={dismissBeta}
              aria-label="Dismiss banner"
              className="p-0.5 rounded text-amber-400 hover:text-amber-200 hover:bg-amber-500/20 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Scrollable page content */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto"
        >
          <div className="mx-auto w-full max-w-screen-xl px-4 py-6 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
